import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegEdit } from 'react-icons/fa';
import { FaRegTrashCan } from 'react-icons/fa6';
import EditIcon from '@components/SvgComponents/EditIcon';
import DeleteIcon from '@components/SvgComponents/DeleteIcon';
import {
    capitalizeFirstLetter,
    formatDateDMY,
    getDataByDeviceId,
    getDataUsingDeviceId,
    getDataUsingUserId,
} from '@utils/sharedUtils';
import { DEVICE_TABLE } from '@constants/dynamoDBTableName';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
    setDeviceDetail,
    setDeviceLog,
    setHospitalDetail,
    setNurseDetail,
    setUserDetail,
} from '@store/reducers/userSlice';
import { useNavigate } from 'react-router-dom';
import { ADMIN, HOSPITAL, INDIVIDUAL, NURSE } from '@constants/constants';
import { HOSPITAL_PATH } from '@constants/routes';
import { getUserInfo, handleFetchLogo } from '@utils/utils';
import DeviceDetailsModal from './DeviceDetailsModal';
interface ActivityLogTableProps {
    data: any[];
    header: any[];
}

interface NotificationData {
    notificationDate: string;
    message: string;
    status?: string;
}

const ActivityLogTable = ({ data, header }: ActivityLogTableProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const loggedInUserRole = useAppSelector(
        (state) => state.login.loggedInUserRole
    );
    const [showModal, setShowModal] = useState(false);

    if (data.length === 0) {
        return (
            <div className="card">
                <div className="card-datatable table-responsive">
                    <table className="datatables-users table">
                        <thead className="border-bottom">
                            <tr>
                                {header.map((notification) => (
                                    <th
                                        className="font-semibold text-black text-base border-none !normal-case"
                                        key={notification.id}
                                    >
                                        {capitalizeFirstLetter(
                                            notification.title
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="text-center ">
                                <td colSpan={header.length + 1}>
                                    {t('user_management.user_table.no_data')}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    let keys = Object.keys(data[0]).filter(
        (key) => key !== 'id' && key != 'created_at' && key != 'user_id'
    );

    const handleDeviceIdClick = async (logList: any) => {
        try {
            const device = await getDataUsingDeviceId(
                DEVICE_TABLE,
                logList.device_id
            );

            if (device.Items && device.Items.length > 0) {
                const deviceData = device.Items[0];
                const createdBy = deviceData.created_by.S
                    ? await getDataUsingUserId(
                          'user_info',
                          deviceData.created_by.S
                      )
                    : '';

                if (
                    createdBy &&
                    createdBy.Items &&
                    createdBy.Items.length > 0
                ) {
                    const additionalDetails = createdBy.Items[0];
                    const mergedUserDetails = {
                        device_uuid: deviceData.uuid.S || '',
                        device_id: deviceData.device_id.S || '',
                        device_name: deviceData.device_name.S || '',
                        device_status: deviceData.device_status.S || '',
                        status: deviceData.status.S || '',
                        created_at:
                            formatDateDMY(deviceData.created_at.S) || '',
                        created_by: additionalDetails?.name?.S
                            ? additionalDetails?.name?.S
                            : '',
                    };

                    dispatch(setDeviceDetail(mergedUserDetails));
                    navigate(`/management/device/${deviceData.device_name.S}`);
                }
            }
        } catch (error: any) {
            console.log('error', error);
        }
    };

    const handleViewClick = async (logList: any) => {
        try {
            const device = await getDataUsingDeviceId(
                DEVICE_TABLE,
                logList.device_id
            );

            const createdBy = await getDataUsingUserId(
                'user_info',
                logList.user_id
            );

            if (createdBy && createdBy.Items && createdBy.Items.length > 0) {
                const additionalDetails = createdBy.Items[0];

                const roleLabel =
                    additionalDetails?.role?.S?.toLowerCase() ===
                    HOSPITAL.toLowerCase()
                        ? 'Hospital Admin'
                        : additionalDetails?.role?.S || '';

                if (device.Items && device.Items.length > 0) {
                    const deviceData = device.Items[0];
                    const mergedUserDetails = {
                        id: logList.id || '',
                        device_id: deviceData.device_id.S || '',
                        device_name: deviceData.device_name.S || '',
                        created_at: formatDateDMY(logList.created_at) || '',
                        created_by: additionalDetails?.name?.S
                            ? additionalDetails?.name?.S
                            : '',
                        role: roleLabel,
                        time: logList.time,
                    };

                    dispatch(setDeviceLog(mergedUserDetails));
                    // navigate(`/device-log/${deviceData.device_name.S}`);
                    setShowModal(true);
                }
            }
        } catch (error: any) {
            console.log('error', error);
        }
    };

    const handleNameClick = async (user: any) => {
        const res = await getDataUsingUserId('user_info', user.user_id);
        if (res && res.Items && res.Items.length > 0) {
            const additionalDetails = res.Items[0];
            if (!additionalDetails) {
                return;
            }
            if (
                additionalDetails.role.S &&
                additionalDetails.role.S.toLowerCase() == HOSPITAL.toLowerCase()
            ) {
                if (loggedInUserRole.toLowerCase() != ADMIN.toLowerCase()) {
                    navigate(HOSPITAL_PATH);
                } else {
                    const hospitalRes = await getUserInfo(
                        'hospital_info',
                        additionalDetails.tenant_id.S || ''
                    );

                    // Extracting additional details from the response
                    if (hospitalRes) {
                        const additionalDetail = hospitalRes[0];

                        // console.log(
                        //     'additionalDetails.hospital_logo?.S || ',
                        //     additionalDetails.hospital_logo?.S || ''
                        // );

                        const logo = await handleFetchLogo(
                            additionalDetails.hospital_logo?.S || ''
                        );

                        const mergedUserDetails = {
                            id: additionalDetails.uuid.S || '',
                            tenantUUid: additionalDetails.tenant_id.S || '',
                            device_id: '',
                            hospital_id: additionalDetails.hospital_id?.S
                                ? additionalDetails.hospital_id?.S
                                : '--',
                            hospital_name: additionalDetail.hospital_name?.S
                                ? additionalDetail.hospital_name?.S
                                : '--',
                            hospital_branch: additionalDetail.hospital_branch?.S
                                ? additionalDetail.hospital_branch?.S
                                : '--',
                            hospital_website: additionalDetail.hospital_website
                                ?.S
                                ? additionalDetail.hospital_website?.S
                                : '--',
                            gst_no: additionalDetail.gst?.S
                                ? additionalDetail.gst?.S
                                : '--',
                            contact_no: additionalDetail.hospital_contact?.S
                                ? additionalDetail.hospital_contact?.S
                                : '--',
                            email_id: additionalDetail.hospital_email?.S
                                ? additionalDetail.hospital_email?.S
                                : '--',
                            address:
                                additionalDetails && additionalDetails.address1
                                    ? `${additionalDetails.address1?.S || ''}, ${additionalDetails.address2?.S || ''}, ${additionalDetails.city?.S || ''}, ${additionalDetails.state?.S || ''}, ${additionalDetails.country?.S || ''} - ${additionalDetails.pincode?.S || ''}`.trim()
                                    : '--',
                            admin_name: additionalDetails.name.S || '',
                            mobile_no: additionalDetails.mobile_number.S || '',
                            admin_email: additionalDetails.email.S || '',
                            create_date: additionalDetails.created_at.S
                                ? formatDateDMY(additionalDetails.created_at.S)
                                : '',
                            hospital_admin_status: additionalDetails.status.S
                                ? capitalizeFirstLetter(
                                      additionalDetails.status.S
                                  )
                                : '--',
                            logo: logo || null,
                            approvedOn: additionalDetails.approved_on?.S
                                ? new Date(additionalDetails.approved_on.S).toLocaleDateString('en-GB')
                                : '--',
                        };
                        dispatch(setHospitalDetail(mergedUserDetails));
                        navigate(
                            `/hospital-users/${additionalDetails.slug?.S}`
                        );
                    }
                }
            } else if (
                additionalDetails.role.S &&
                additionalDetails.role.S.toLowerCase() == NURSE.toLowerCase()
            ) {
                if (loggedInUserRole.toLowerCase() != ADMIN.toLowerCase()) {
                    let approved_By = '--';
                    if (additionalDetails.approved_by?.S) {
                        const assigned = await getDataUsingUserId(
                            'user_info',
                            additionalDetails.approved_by?.S
                                ? additionalDetails.approved_by?.S
                                : ''
                        );

                        if (
                            assigned &&
                            assigned.Items &&
                            assigned.Items.length > 0
                        ) {
                            const data = assigned.Items[0];
                            approved_By = data.name?.S ? data.name?.S : '';
                        }
                    }

                    const created = await getDataUsingUserId(
                        'user_info',
                        additionalDetails.created_by?.S
                            ? additionalDetails.created_by?.S
                            : ''
                    );

                    let created_By = '--';
                    if (created && created.Items && created.Items.length > 0) {
                        const userdata = created.Items[0];
                        created_By = userdata.name?.S ? userdata.name?.S : '';
                    }

                    const mergedUserDetails = {
                        id: additionalDetails.uuid.S
                            ? additionalDetails.uuid.S
                            : '',
                        tenantUUid: additionalDetails.tenant_id?.S
                            ? additionalDetails.tenant_id?.S
                            : '',
                        name: additionalDetails.name.S
                            ? additionalDetails.name.S
                            : '',
                        status: additionalDetails?.status?.S
                            ? additionalDetails?.status?.S
                            : '--',
                        hospitalId: additionalDetails.hospital_id?.S
                            ? additionalDetails.hospital_id?.S
                            : '--',
                        hospitalName: additionalDetails.hospital_name?.S
                            ? additionalDetails.hospital_name?.S
                            : '--',
                        email_id: additionalDetails.email.S
                            ? additionalDetails.email.S
                            : '',
                        contact_id: additionalDetails.mobile_number.S
                            ? additionalDetails.mobile_number.S
                            : '',
                        approvedBy: approved_By,
                        createdBy: created_By,
                        createdAt: additionalDetails.created_at.S
                            ? formatDateDMY(additionalDetails.created_at.S)
                            : '',
                        nurseId: additionalDetails.nurse_id?.S
                            ? additionalDetails.nurse_id?.S
                            : '',
                        address:
                            additionalDetails && additionalDetails.address1
                                ? `${additionalDetails.address1?.S || ''}, ${additionalDetails.address2?.S || ''}, ${additionalDetails.city?.S || ''}, ${additionalDetails.state?.S || ''}, ${additionalDetails.country?.S || ''} - ${additionalDetails.pincode?.S || ''}`.trim()
                                : '--',
                    };
                    dispatch(setNurseDetail(mergedUserDetails));
                    navigate(`/management/nurse/${user.slug}`);
                } else {
                    let approved_By = '--';
                    if (additionalDetails.approved_by?.S) {
                        const assigned = await getDataUsingUserId(
                            'user_info',
                            additionalDetails.approved_by?.S
                                ? additionalDetails.approved_by?.S
                                : ''
                        );

                        if (
                            assigned &&
                            assigned.Items &&
                            assigned.Items.length > 0
                        ) {
                            const data = assigned.Items[0];
                            approved_By = data.name?.S ? data.name?.S : '';
                        }
                    }

                    const created = await getDataUsingUserId(
                        'user_info',
                        additionalDetails.created_by?.S
                            ? additionalDetails.created_by?.S
                            : ''
                    );

                    let created_By = '--';
                    if (created && created.Items && created.Items.length > 0) {
                        const userdata = created.Items[0];
                        created_By = userdata.name?.S ? userdata.name?.S : '';
                    }

                    const mergedUserDetails = {
                        id: additionalDetails.uuid?.S
                            ? additionalDetails.uuid?.S
                            : '',
                        tenantUUid: additionalDetails.tenant_id?.S
                            ? additionalDetails.tenant_id?.S
                            : '',
                        name: additionalDetails.name.S || '',
                        status: additionalDetails?.status.S
                            ? additionalDetails?.status.S
                            : '--',
                        deviceId: '',
                        hospitalId: additionalDetails.hospital_id?.S
                            ? additionalDetails.hospital_id?.S
                            : '--',
                        hospitalName: additionalDetails.hospital_name?.S
                            ? additionalDetails.hospital_name?.S
                            : '--',
                        email_id: additionalDetails.email.S || '',
                        contact_id: additionalDetails.mobile_number.S || '',
                        approvedBy: approved_By,
                        createdBy: created_By,
                        createdAt: additionalDetails.created_at.S
                            ? formatDateDMY(additionalDetails.created_at.S)
                            : '',
                        nurseId: additionalDetails.nurse_id?.S
                            ? additionalDetails.nurse_id?.S
                            : '',
                        address:
                            additionalDetails && additionalDetails.address1
                                ? `${additionalDetails.address1?.S || ''}, ${additionalDetails.address2?.S || ''}, ${additionalDetails.city?.S || ''}, ${additionalDetails.state?.S || ''}, ${additionalDetails.country?.S || ''} - ${additionalDetails.pincode?.S || ''}`.trim()
                                : '--',
                    };
                    dispatch(setNurseDetail(mergedUserDetails));
                    navigate(`/nurse-users/${additionalDetails.slug.S}`);
                }
            } else if (
                additionalDetails.role.S &&
                additionalDetails.role.S.toLowerCase() ==
                    INDIVIDUAL.toLowerCase()
            ) {
                const mergedUserDetails = {
                    id: user.id,
                    tenantUUid: additionalDetails.tenant_id.S || '',
                    name: additionalDetails.name.S || '',
                    device_id: '',
                    email_id: additionalDetails.email.S || '',
                    contact_id: additionalDetails.mobile_number.S || '',
                    create_date: additionalDetails.created_at.S
                        ? formatDateDMY(additionalDetails.created_at.S)
                        : '',
                    status: additionalDetails?.status?.S
                        ? additionalDetails?.status?.S.toLowerCase() !=
                          'blocked'
                            ? user.userEmailStatus
                            : additionalDetails?.status?.S
                        : '--',
                    address:
                        additionalDetails && additionalDetails.address1
                            ? `${additionalDetails.address1?.S || ''}, ${additionalDetails.address2?.S || ''}, ${additionalDetails.city?.S || ''}, ${additionalDetails.state?.S || ''}, ${additionalDetails.country?.S || ''} - ${additionalDetails.pincode?.S || ''}`.trim()
                            : '--',
                };
                // console.log('mergedUserDetails', mergedUserDetails);
                dispatch(setUserDetail(mergedUserDetails));
                navigate(`/individual-users/${additionalDetails.slug.S}`);
            }
        }
    };

    return (
        <>
            <div className="card rounded-2xl mt-5">
                <div className="card-datatable table-responsive">
                    <table className="datatables-users table">
                        <thead className="border-bottom">
                            <tr>
                                <th className=""></th>
                                {header.map((list) => (
                                    <th
                                        key={list.id}
                                        className={`text-black text-base font-semibold py-4 !normal-case
                                                    ${
                                                        list.title.toLowerCase() ===
                                                        'action'
                                                            ? 'text-center'
                                                            : ''
                                                    }
                                                    `}
                                    >
                                        {capitalizeFirstLetter(list.title)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((logList) => (
                                <tr key={logList.id} style={{ width: '264px' }}>
                                    <td className=""></td>
                                    {keys.map((key) => {
                                        const value = logList[key];

                                        const isDeviceIdClickable =
                                            key === 'device_id' &&
                                            loggedInUserRole.toLowerCase() ===
                                                HOSPITAL.toLowerCase();
                                        const isNameClickable =
                                            (key === 'name' &&
                                                loggedInUserRole.toLowerCase() ===
                                                    HOSPITAL.toLowerCase()) ||
                                            (key === 'name' &&
                                                loggedInUserRole.toLowerCase() ===
                                                    ADMIN.toLowerCase());

                                        return (
                                            <td
                                                key={key}
                                                className={`${isDeviceIdClickable || isNameClickable ? 'cursor-pointer' : ''}`}
                                                onClick={
                                                    isDeviceIdClickable
                                                        ? () =>
                                                              handleDeviceIdClick(
                                                                  logList
                                                              )
                                                        : isNameClickable
                                                          ? () =>
                                                                handleNameClick(
                                                                    logList
                                                                )
                                                          : undefined
                                                }
                                            >
                                                <div
                                                    className={`text-base font-normal text-table_header`}
                                                >
                                                    {value ?? 'N/A'}
                                                </div>
                                            </td>
                                        );
                                    })}
                                    <td>
                                        <div className="text-center">
                                            <div
                                                className="cursor-pointer btn text-white bg-primary py-2 px-3"
                                                onClick={() =>
                                                    handleViewClick(logList)
                                                }
                                            >
                                                {t('device_details.view')}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            <tr></tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <DeviceDetailsModal
                show={showModal}
                onHide={() => setShowModal(false)}
            />
        </>
    );
};
export default ActivityLogTable;
